import { Controller } from "@hotwired/stimulus"

// LE DIAPORAMA : deux boutons, et le défilement automatique quand il est demandé.
//
// LE DÉFILEMENT LUI-MÊME EST NATIF — `scroll-snap` en CSS, l'inertie et le glissement au doigt sont
// portés par le navigateur. Ce contrôleur ne fait que déplacer la piste et gérer le minuteur : sans
// lui, les images défilent toujours au doigt et à la molette, seuls les boutons manquent. C'est
// pourquoi ils sont masqués au rendu et révélés ici.
//
// Aucune bibliothèque : le projet de référence embarque Swiper, plusieurs dizaines de kilo-octets
// sur le chemin de chaque page qui affiche un diaporama.
export default class extends Controller {
  static targets = ["track", "slide", "control"]
  static values = {
    autoplay: Boolean,
    delay: { type: Number, default: 4000 },
    looping: { type: Boolean, default: true }
  }

  connect() {
    // Les boutons n'apparaissent qu'ici : sans JavaScript ils n'auraient rien commandé, et un
    // bouton mort est pire qu'un bouton absent.
    this.controlTargets.forEach((control) => { control.hidden = false })

    if (this.autoplayValue) this.#startAutoplay()
  }

  disconnect() {
    this.#stopAutoplay()
  }

  next() {
    // LE GESTE MANUEL ARRÊTE LE DÉFILEMENT AUTOMATIQUE, définitivement : reprendre la main puis se
    // faire déplacer trois secondes plus tard est le comportement qu'on reproche à ces objets.
    this.#stopAutoplay()
    this.#goTo(this.#currentIndex() + 1)
  }

  previous() {
    this.#stopAutoplay()
    this.#goTo(this.#currentIndex() - 1)
  }

  #goTo(index) {
    const last = this.slideTargets.length - 1
    let targetIndex = index

    if (index > last) targetIndex = this.loopingValue ? 0 : last
    if (index < 0) targetIndex = this.loopingValue ? last : 0

    // `scrollTo` sur la piste plutôt que `scrollIntoView` sur la vue : celui-ci fait aussi défiler
    // la PAGE quand le diaporama n'est pas entièrement visible, ce qui déplace la lecture sans
    // qu'on l'ait demandé.
    //
    // `smooth` NE SE VÉRIFIE PAS AU NAVIGATEUR PILOTÉ, et il faut le savoir avant de croire ce
    // fichier cassé. Le 21 août, un clic réel sur les flèches ne déplaçait la piste que de trois
    // pixels, ce qui ressemblait à un défaut de ce contrôleur. La sonde qui a tranché : une page
    // sans une ligne de JavaScript, un lien `href="#cible"` et `scroll-behavior: smooth` en CSS —
    // le clic change l'adresse et la page ne bouge pas davantage. Deux mesures l'ont confirmé au
    // passage : une transition CSS y saute d'un état à l'autre sans intermédiaire, et l'API Web
    // Animations n'anime rien, alors que `prefers-reduced-motion` vaut `false`.
    //
    // AUTREMENT DIT : un défilement animé ne peut pas être observé depuis l'outil d'automatisation,
    // et son absence n'y prouve rien. Se fier à cette observation coûterait l'animation pour tous
    // les visiteurs, afin de réparer ce qui n'est cassé que sur le banc d'essai.
    this.trackTarget.scrollTo({ left: this.slideTargets[targetIndex].offsetLeft - this.trackTarget.offsetLeft,
                                behavior: "smooth" })
  }

  // La vue en place : celle dont le bord gauche est le plus proche du défilement courant. Lu à
  // chaque geste plutôt que mémorisé — le doigt et la molette déplacent la piste sans nous prévenir,
  // et un compteur interne se serait désynchronisé au premier glissement.
  #currentIndex() {
    const position = this.trackTarget.scrollLeft
    const distances = this.slideTargets.map(
      (slide) => Math.abs(slide.offsetLeft - this.trackTarget.offsetLeft - position)
    )
    return distances.indexOf(Math.min(...distances))
  }

  #startAutoplay() {
    this.timer = setInterval(() => this.#goTo(this.#currentIndex() + 1), this.delayValue)
  }

  #stopAutoplay() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }
}
